/**
 * Component: Club Rental
 * Description: "Add-Ons (optional)" accordion for reservation PDPs. Each rental type has one
 *              quantity stepper and a multi-select handedness toggle (left and/or right).
 *              The quantity is per selected side; its ceiling is the selected side's inventory,
 *              or — when both sides are picked — the scarcer side (min of the two), since each
 *              side is booked equally. Maximums come from data-max (product.club_inventory).
 *              At submit, global.js pushes the per-side quantity once per selected side into the
 *              club_rental variant-id array.
 */
(() => {
  const MIN_QTY = 0;
  const STEP_UP = 1;
  const CENTS_PER_UNIT = 100;

  const PRICES_EVENT = 'club-rental:prices';

  class ProductClubRental extends HTMLElement {
    constructor() {
      super();
      this.header = this.querySelector('.club-rental__header');
      this.content = this.querySelector('.club-rental__content');
      this.items = this.querySelectorAll('[data-club-rental-item]');

      this.refreshPrices = this.refreshPrices.bind(this);
    }

    connectedCallback() {
      this.header?.addEventListener('click', this.toggleAccordion.bind(this));
      this.items.forEach((item) => this.initItem(item));

      document.addEventListener(PRICES_EVENT, this.refreshPrices);
      this.refreshPrices();
    }

    disconnectedCallback() {
      document.removeEventListener(PRICES_EVENT, this.refreshPrices);
    }

    toggleAccordion() {
      const isOpen = this.header.getAttribute('aria-expanded') === 'true';
      this.header.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      this.content.hidden = isOpen;
    }

    initItem(item) {
      item.querySelectorAll('[data-club-rental-step]').forEach((button) => {
        button.addEventListener('click', () => {
          this.handleStep(item, parseInt(button.dataset.clubRentalStep, 10));
        });
      });

      item.querySelectorAll('[data-club-rental-hand]').forEach((button) => {
        button.addEventListener('click', () => this.toggleHandedness(item, button));
      });

      this.updateStepButtons(item);
    }

    getQtyInput(item) {
      return item.querySelector('[data-club-rental-qty]');
    }

    getQtyValue(item) {
      return parseInt(this.getQtyInput(item).value, 10) || MIN_QTY;
    }

    getSelectedHands(item) {
      return item.querySelectorAll('[data-club-rental-hand][aria-pressed="true"]');
    }

    getFirstAvailableHand(item) {
      return item.querySelector('[data-club-rental-hand]:not([disabled])');
    }

    getMaxForItem(item) {
      const selected = this.getSelectedHands(item);
      let hands = Array.from(selected);

      // No side chosen yet — the first in-stock side sets the ceiling for the 0 -> 1 step.
      if (!hands.length) {
        const firstAvailable = this.getFirstAvailableHand(item);
        hands = firstAvailable ? [firstAvailable] : [];
      }

      if (!hands.length) return MIN_QTY;

      // Booking both sides at once is capped by the scarcer side.
      return hands.reduce((min, hand) => {
        return Math.min(min, parseInt(hand.dataset.max, 10) || MIN_QTY);
      }, Infinity);
    }

    handleStep(item, delta) {
      const input = this.getQtyInput(item);
      const current = this.getQtyValue(item);

      // Leaving 0 with nothing chosen — pick the first in-stock side.
      if (delta === STEP_UP && current === MIN_QTY && !this.getSelectedHands(item).length) {
        const firstAvailable = this.getFirstAvailableHand(item);
        if (!firstAvailable) return;
        this.setHandPressed(firstAvailable, true);
      }

      const max = this.getMaxForItem(item);
      const next = Math.min(max, Math.max(MIN_QTY, current + delta));
      if (next === current) return;

      input.value = next;

      if (next === MIN_QTY) {
        this.clearHandedness(item);
      } else {
        this.showHandedness(item);
      }

      this.updateStepButtons(item);
      this.emitChange();
    }

    toggleHandedness(item, button) {
      if (button.disabled) return;

      const isPressed = button.getAttribute('aria-pressed') === 'true';

      // Keep at least one side selected while a quantity is set.
      if (isPressed && this.getSelectedHands(item).length === 1 && this.getQtyValue(item) > MIN_QTY) {
        return;
      }

      this.setHandPressed(button, !isPressed);

      // Re-cap against the new selection (both sides -> scarcer side).
      this.updateStepButtons(item);
      this.emitChange();
    }

    // Notifies listeners (e.g. reservation-summary) that add-on selections changed.
    emitChange() {
      this.dispatchEvent(new CustomEvent('club-rental:change', { bubbles: true }));
    }

    reset() {
      this.items.forEach((item) => {
        const input = this.getQtyInput(item);
        if (input) input.value = MIN_QTY;
        this.clearHandedness(item);
        this.updateStepButtons(item);
      });
      this.emitChange();
    }

    refreshPrices() {
      this.items.forEach((item) => this.refreshItemPrice(item));
    }

    refreshItemPrice(item) {
      const priceWrapper = item.querySelector('[data-club-rental-price]');
      const amountTarget = item.querySelector('[data-club-rental-price-amount]');
      if (!priceWrapper || !amountTarget) return;

      const hand = item.querySelector('[data-club-rental-hand][data-price]');
      const cents = hand ? parseInt(hand.dataset.price, 10) : NaN;

      if (!Number.isFinite(cents) || cents <= 0) {
        priceWrapper.hidden = true;
        return;
      }

      amountTarget.textContent = this.formatPrice(cents);
      priceWrapper.hidden = false;
    }

    formatPrice(cents) {
      let money;
      if (window.theme?.Currency?.formatMoney && window.shopSettings?.moneyFormat) {
        money = window.theme.Currency.formatMoney(cents, window.shopSettings.moneyFormat);
      } else {
        money = `$${(cents / CENTS_PER_UNIT).toFixed(2)}`;
      }

      return cents % CENTS_PER_UNIT === 0 ? money.replace(/[.,]00(?=\D*$)/, '') : money;
    }

    setHandPressed(button, pressed) {
      button.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    }

    showHandedness(item) {
      const group = item.querySelector('.club-rental__handedness');
      if (group) group.hidden = false;
    }

    clearHandedness(item) {
      const group = item.querySelector('.club-rental__handedness');
      if (group) group.hidden = true;
      item.querySelectorAll('[data-club-rental-hand]').forEach((hand) => {
        hand.setAttribute('aria-pressed', 'false');
      });
    }

    updateStepButtons(item) {
      const input = this.getQtyInput(item);
      const max = this.getMaxForItem(item);
      let value = this.getQtyValue(item);

      // Never let a quantity sit above the current ceiling.
      if (value > max) {
        value = max;
        input.value = value;
      }

      const stepper = item.querySelector('[data-club-rental-stepper]');

      input.max = max;
      stepper?.classList.toggle('_active', value > MIN_QTY);

      item.querySelectorAll('[data-club-rental-step]').forEach((button) => {
        const delta = parseInt(button.dataset.clubRentalStep, 10);
        const projected = value + delta;
        button.disabled = projected < MIN_QTY || projected > max;
      });
    }
  }

  customElements.define('product-club-rental', ProductClubRental);
})();
